import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';
import { useNotification } from '../../contexts/NotificationContext';
import { APP_VERSION } from '../../config/appConfig';

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { showNotification } = useNotification();
  const navigate = useNavigate();

  const handleLinkClick = (e) => {
    e.preventDefault();
    showNotification('This feature is coming soon!', 'info');
  };

  const handleQuickLinkClick = (e, sectionId) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  if (isMobile) {
    return (
      <Box
        component="footer"
        sx={{
          bgcolor: 'background.paper',
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          width: '100%',
          position: 'relative',
          zIndex: 1
        }}
      >
        <Container maxWidth="lg">
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <IconButton color="primary" size="small" onClick={handleLinkClick}>
              <FacebookIcon />
            </IconButton>
            <IconButton color="primary" size="small" onClick={handleLinkClick}>
              <TwitterIcon />
            </IconButton>
            <IconButton color="primary" size="small" onClick={handleLinkClick}>
              <InstagramIcon />
            </IconButton>
          </Stack>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            align="center" 
            sx={{ mt: 1 }}
          >
            © {new Date().getFullYear()} CircleMoments. All rights reserved.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            align="center"
            sx={{ display: 'block', mt: 0.5 }}
          >
            {APP_VERSION}
          </Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        py: 6,
        borderTop: 1,
        borderColor: 'divider',
        width: '100%',
        position: 'relative',
        zIndex: 1,
        padding: '30px 50px'
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={4} justifyContent="space-between">
          {/* Logo and Slogan */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 2
              }}
            >
              <img
                src="/logo.png"
                alt="CircleMoments Logo"
                style={{
                  height: '40px',
                  marginRight: '12px'
                }}
              />
              <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600 }}>
                CircleMoments
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your Events Understood
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton color="primary" size="small" onClick={handleLinkClick}>
                <FacebookIcon />
              </IconButton>
              <IconButton color="primary" size="small" onClick={handleLinkClick}>
                <TwitterIcon />
              </IconButton>
              <IconButton color="primary" size="small" onClick={handleLinkClick}>
                <InstagramIcon />
              </IconButton>
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid size={{ xs: 12, md: 2 }}>
            <Typography 
              variant="subtitle1" 
              color="text.primary" 
              gutterBottom 
              sx={{ fontWeight: 600 }}
            >
              Quick Links
            </Typography>
            <Stack spacing={1}>
              <Link 
                component={RouterLink} 
                to="/#home" 
                color="text.secondary" 
                onClick={(e) => handleQuickLinkClick(e, 'home')}
                sx={{ 
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s'
                }}
              >
                Home
              </Link>
              <Link 
                component={RouterLink} 
                to="/#about" 
                color="text.secondary"
                onClick={(e) => handleQuickLinkClick(e, 'about')}
                sx={{ 
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s'
                }}
              >
                About
              </Link>
              <Link 
                component={RouterLink} 
                to="/#features" 
                color="text.secondary"
                onClick={(e) => handleQuickLinkClick(e, 'features')}
                sx={{ 
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s'
                }}
              >
                Features
              </Link>
              <Link 
                component={RouterLink} 
                to="/#how-it-works" 
                color="text.secondary"
                onClick={(e) => handleQuickLinkClick(e, 'how-it-works')}
                sx={{ 
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s'
                }}
              >
                How It Works
              </Link>
            </Stack>
          </Grid>

          {/* Contact */}
          <Grid size={{ xs: 12, md: 2 }}>
            <Typography 
              variant="subtitle1" 
              color="text.primary" 
              gutterBottom 
              sx={{ fontWeight: 600 }}
            >
              Contact Us
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                skanda@circlemoments.com
              </Typography>
              <Typography variant="body2" color="text.secondary">
                London, United Kingdom
              </Typography>
            </Stack>
          </Grid>

          {/* Download Links */}
          <Grid size={{ xs: 12, md: 2 }}>
            <Typography 
              variant="subtitle1" 
              color="text.primary" 
              gutterBottom 
              sx={{ fontWeight: 600 }}
            >
              Download App
            </Typography>
            <Stack spacing={1}>
              <Link 
                href="#" 
                color="text.secondary"
                onClick={handleLinkClick}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s'
                }}
              >
                <AppleIcon fontSize="small" />
                iOS App Store
              </Link>
              <Link 
                href="#" 
                color="text.secondary"
                onClick={handleLinkClick}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s'
                }}
              >
                <AndroidIcon fontSize="small" />
                Google Play
              </Link>
              <Link 
                href="#" 
                color="text.secondary"
                onClick={handleLinkClick}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  '&:hover': { color: 'primary.main' },
                  transition: 'color 0.2s'
                }}
              >
                <DesktopWindowsIcon fontSize="small" />
                Desktop App
              </Link>
            </Stack>
          </Grid>
        </Grid>

        <Box 
          sx={{ 
            mt: 5, 
            borderTop: 1, 
            borderColor: 'divider', 
            pt: 3,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} CircleMoments. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {APP_VERSION}
            </Typography>
            <Link 
              href="/privacy.html"
              color="text.secondary" 
              variant="body2"
              sx={{ 
                '&:hover': { color: 'primary.main' },
                transition: 'color 0.2s'
              }}
            >
              Privacy
            </Link>
            <Link 
              href="/terms.html"
              color="text.secondary" 
              variant="body2"
              sx={{ 
                '&:hover': { color: 'primary.main' },
                transition: 'color 0.2s'
              }}
            >
              Terms
            </Link>
            <Link 
              component={RouterLink} 
              to="/cookies" 
              color="text.secondary" 
              variant="body2"
              onClick={handleLinkClick}
              sx={{ 
                '&:hover': { color: 'primary.main' },
                transition: 'color 0.2s'
              }}
            >
              Cookies
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
};

export default Footer; 
